'use client'

import { memo, useState } from 'react'
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '@/lib/utils'
import { copyImageToClipboard, getImageFormat, normalizeDataUrl, MEDIA_PREVIEW_HEIGHT } from '@/lib/image-utils'
import { ImageOff, Link, Image, Check, X } from 'lucide-react'
import { MermaidDiagram } from './mermaid'
import { MediaPreview } from './media-preview'

interface MarkdownProps {
  content: string
  className?: string
}

// Custom URL transform that allows data:image URLs (for base64 images including SVG)
// while still blocking dangerous URLs like javascript: and data:text/html
function customUrlTransform(url: string): string {
  if (url.startsWith('data:image/')) {
    return url
  }
  return defaultUrlTransform(url)
}

// Module-level cache for failed image URLs to persist across re-renders
const failedImageUrls = new Set<string>()

// Image component with wrapper, copy buttons, and preview
function MarkdownImage({ src, alt }: React.ImgHTMLAttributes<HTMLImageElement>) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [copiedUrl, setCopiedUrl] = useState(false)
  const [copiedImage, setCopiedImage] = useState(false)
  const [copyError, setCopyError] = useState(false)
  const [failed, setFailed] = useState(false)
  const [errorReason, setErrorReason] = useState<string>('')

  // Normalize the source URL - especially important for data URLs with whitespace
  const srcUrl = typeof src === 'string' ? normalizeDataUrl(src.trim()) : undefined

  const format = srcUrl ? getImageFormat(srcUrl) : 'Image'
  const isDataUrl = srcUrl?.startsWith('data:')

  // Helper to copy URL even in error state
  const handleCopyUrlInError = async () => {
    if (!srcUrl) return
    try {
      await navigator.clipboard.writeText(srcUrl)
      setCopiedUrl(true)
      setTimeout(() => setCopiedUrl(false), 2000)
    } catch (err) {
      console.error('Failed to copy URL:', err)
    }
  }

  // Get display URL (truncated for long data URLs)
  const getDisplayUrl = () => {
    if (!srcUrl) return 'No URL'
    if (isDataUrl) {
      return `data:image/... (${Math.round(srcUrl.length / 1024)}KB)`
    }
    if (srcUrl.length > 50) {
      return srcUrl.substring(0, 47) + '...'
    }
    return srcUrl
  }

  // Check cache first - if URL already failed, show error state
  // Don't use cache for data URLs - they should render if valid
  if (!src || (!isDataUrl && srcUrl && failedImageUrls.has(srcUrl)) || failed) {
    const displayError = errorReason || (src ? 'Failed to load' : 'No image source')
    return (
      <span className="block max-w-full rounded-lg overflow-hidden bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800/50 my-2">
        <span className="flex items-center justify-between gap-1 px-2 py-1.5 border-b border-red-200 dark:border-red-800/50">
          <span className="text-xs text-red-600 dark:text-red-400">{format} - Error</span>
          {srcUrl && (
            <button
              onClick={handleCopyUrlInError}
              className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors text-red-500 hover:text-red-700 dark:text-red-400"
              title="Copy URL"
            >
              {copiedUrl ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Link className="h-3.5 w-3.5" />}
            </button>
          )}
        </span>
        <span
          className="flex items-center justify-center text-red-600 dark:text-red-400"
          style={{ height: MEDIA_PREVIEW_HEIGHT }}
        >
          <span className="flex flex-col items-center gap-2 px-4">
            <ImageOff className="h-8 w-8 opacity-50" />
            <span className="text-sm font-medium">{displayError}</span>
            <span className="text-xs opacity-70 text-center break-all max-w-[300px]">{getDisplayUrl()}</span>
          </span>
        </span>
      </span>
    )
  }

  const handleCopyUrl = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!srcUrl) return
    try {
      await navigator.clipboard.writeText(srcUrl)
      setCopiedUrl(true)
      setTimeout(() => setCopiedUrl(false), 2000)
    } catch (err) {
      console.error('Failed to copy URL:', err)
    }
  }

  const handleCopyImage = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!srcUrl) return
    const result = await copyImageToClipboard(srcUrl)
    if (result.success) {
      setCopiedImage(true)
      setTimeout(() => setCopiedImage(false), 2000)
    } else {
      setCopyError(true)
      setTimeout(() => setCopyError(false), 2000)
    }
  }

  const handleError = () => {
    // Don't cache data URLs - they're unique and won't be retried
    if (srcUrl && !isDataUrl) failedImageUrls.add(srcUrl)
    // Set appropriate error message
    if (isDataUrl) {
      setErrorReason('Invalid or corrupted image data')
    } else if (srcUrl?.includes('localhost') || srcUrl?.includes('127.0.0.1')) {
      setErrorReason('Cannot load from localhost')
    } else {
      setErrorReason('Image failed to load')
    }
    setFailed(true)
  }

  return (
    <>
      <span className="block max-w-full rounded-lg overflow-hidden bg-zinc-100 dark:bg-zinc-800/50 my-2">
        <span className="flex items-center justify-between gap-1 px-2 py-1.5 border-b border-zinc-200 dark:border-zinc-700/50">
          <span className="text-xs text-muted-foreground">{format}</span>
          <span className="flex items-center gap-1">
            <button
              onClick={handleCopyImage}
              className="p-1.5 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded transition-colors text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
              title="Copy image"
            >
              {copyError ? <X className="h-4 w-4 text-red-500" /> : copiedImage ? <Check className="h-4 w-4 text-green-500" /> : <Image className="h-4 w-4" />}
            </button>
            <button
              onClick={handleCopyUrl}
              className="p-1.5 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded transition-colors text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
              title="Copy URL"
            >
              {copiedUrl ? <Check className="h-4 w-4 text-green-500" /> : <Link className="h-4 w-4" />}
            </button>
          </span>
        </span>
        <span
          role="button"
          tabIndex={0}
          onClick={() => setIsPreviewOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setIsPreviewOpen(true)
            }
          }}
          className="cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/30 transition-colors outline-none flex items-center justify-center p-3"
          style={{ height: MEDIA_PREVIEW_HEIGHT }}
          title="Click to zoom"
          aria-label="Open image in fullscreen"
        >
          <img
            src={srcUrl}
            alt={alt}
            onError={handleError}
            className="max-h-full max-w-full object-contain"
          />
        </span>
      </span>

      <MediaPreview
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        imageUrl={srcUrl}
        title={alt || 'Image Preview'}
      />
    </>
  )
}

// Memoize components object at module level to prevent recreation on each render
const markdownComponents = {
  img: MarkdownImage,
  pre: ({ children, ...props }: React.HTMLAttributes<HTMLPreElement>) => {
    // Check if this is a mermaid code block - don't wrap with pre styles
    const child = children as React.ReactElement<{ className?: string }>
    if (child?.props?.className?.includes('language-mermaid')) {
      return <>{children}</>
    }
    return (
      <pre
        className="overflow-x-auto rounded-lg bg-zinc-950 dark:bg-zinc-900 p-3 text-sm"
        {...props}
      >
        {children}
      </pre>
    )
  },
  code: ({ className, children, ...props }: React.HTMLAttributes<HTMLElement>) => {
    const isInline = !className
    if (isInline) {
      return (
        <code
          className="rounded bg-zinc-200 dark:bg-zinc-800 px-1.5 py-0.5 text-sm font-mono"
          {...props}
        >
          {children}
        </code>
      )
    }

    // Check for mermaid code blocks
    const match = /language-(\w+)/.exec(className || '')
    const lang = match?.[1]
    if (lang === 'mermaid') {
      const code = String(children).replace(/\n$/, '')
      return <MermaidDiagram code={code} />
    }

    return (
      <code className={cn('text-zinc-100 font-mono text-sm', className)} {...props}>
        {children}
      </code>
    )
  },
  a: ({ children, href, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-500 hover:text-blue-600 underline underline-offset-2"
      {...props}
    >
      {children}
    </a>
  ),
  ul: ({ children, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
    <ul className="list-disc pl-6 my-2 space-y-1" {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, ...props }: React.HTMLAttributes<HTMLOListElement>) => (
    <ol className="list-decimal pl-6 my-2 space-y-1" {...props}>
      {children}
    </ol>
  ),
  li: ({ children, ...props }: React.HTMLAttributes<HTMLLIElement>) => (
    <li className="leading-relaxed" {...props}>
      {children}
    </li>
  ),
  p: ({ children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p className="leading-relaxed my-2 first:mt-0 last:mb-0" {...props}>
      {children}
    </p>
  ),
  h1: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h1 className="text-lg font-bold mt-4 mb-2 first:mt-0" {...props}>
      {children}
    </h1>
  ),
  h2: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h2 className="text-base font-bold mt-4 mb-2 first:mt-0" {...props}>
      {children}
    </h2>
  ),
  h3: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h3 className="text-[15px] font-bold mt-3 mb-1.5 first:mt-0" {...props}>
      {children}
    </h3>
  ),
  blockquote: ({ children, ...props }: React.HTMLAttributes<HTMLQuoteElement>) => (
    <blockquote
      className="border-l-4 border-zinc-300 dark:border-zinc-700 pl-4 italic my-2"
      {...props}
    >
      {children}
    </blockquote>
  ),
  table: ({ children, ...props }: React.HTMLAttributes<HTMLTableElement>) => (
    <div className="overflow-x-auto my-2">
      <table className="min-w-full border-collapse text-sm" {...props}>
        {children}
      </table>
    </div>
  ),
  thead: ({ children, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => (
    <thead className="bg-zinc-100 dark:bg-zinc-800" {...props}>
      {children}
    </thead>
  ),
  th: ({ children, ...props }: React.HTMLAttributes<HTMLTableCellElement>) => (
    <th className="border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-left font-semibold" {...props}>
      {children}
    </th>
  ),
  td: ({ children, ...props }: React.HTMLAttributes<HTMLTableCellElement>) => (
    <td className="border border-zinc-200 dark:border-zinc-700 px-3 py-2" {...props}>
      {children}
    </td>
  ),
  hr: (props: React.HTMLAttributes<HTMLHRElement>) => (
    <hr className="my-4 border-zinc-200 dark:border-zinc-700" {...props} />
  ),
  strong: ({ children, ...props }: React.HTMLAttributes<HTMLElement>) => (
    <strong className="font-semibold" {...props}>
      {children}
    </strong>
  ),
  em: ({ children, ...props }: React.HTMLAttributes<HTMLElement>) => (
    <em className="italic" {...props}>
      {children}
    </em>
  ),
}

// Memoize Markdown component to prevent re-renders when content hasn't changed
export const Markdown = memo(function Markdown({ content, className }: MarkdownProps) {
  return (
    <div className={cn('prose prose-sm dark:prose-invert max-w-full break-words overflow-hidden leading-relaxed', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
        urlTransform={customUrlTransform}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
})
